import {
  LinkOutlined,
  LogoutOutlined,
  SettingOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {
  ModalForm,
  ProFormItem,
  ProFormSelect,
  ProFormText,
  ProFormTextArea,
} from '@ant-design/pro-components';
import { history, useModel } from '@umijs/max';
import type { MenuProps } from 'antd';
import { Avatar, Dropdown, message, Spin, Typography, Upload } from 'antd';
import type { UploadFile } from 'antd/es/upload/interface';
import { createStyles } from 'antd-style';
import { signOut } from 'aws-amplify/auth';
import React, { useState } from 'react';
import { flushSync } from 'react-dom';
import { startLineLink } from '@/services/api/line';
import { uploadImage } from '@/services/api/upload';
import { putUser } from '@/services/api/user';

const ROLE_LABEL: Record<string, string> = {
  admin: '管理者',
  worker: '作業者',
  light: 'ライト',
  standard: 'スタンダード',
  premium: 'プレミアム',
};

export const AvatarName = () => {
  const { initialState } = useModel('@@initialState');
  const user = initialState?.currentUser;
  if (!user) return null;

  return (
    <span>
      {user.name}
      {user.role ? `（${ROLE_LABEL[user.role] ?? user.role}）` : ''}
    </span>
  );
};

async function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export type GlobalHeaderRightProps = {
  menu?: boolean;
  children?: React.ReactNode;
};

const useStyles = createStyles(({ token }) => {
  return {
    action: {
      display: 'flex',
      height: '48px',
      marginLeft: 'auto',
      overflow: 'hidden',
      alignItems: 'center',
      padding: '0 8px',
      cursor: 'pointer',
      borderRadius: token.borderRadius,
      '&:hover': { backgroundColor: token.colorBgTextHover },
    },
  };
});

export const AvatarDropdown: React.FC<GlobalHeaderRightProps> = ({
  children,
}) => {
  const { styles } = useStyles();
  const { initialState, setInitialState } = useModel('@@initialState');
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [previewUrl, setPreviewUrl] = useState<string | undefined>();
  const [uploading, setUploading] = useState(false);

  const loginPath = '/login';

  const doLogout = async () => {
    try {
      await signOut();
    } catch (e) {
      console.error(e);
      message.warning(
        'ログアウト処理で問題が発生しました。ログイン画面に戻ります。',
      );
    } finally {
      flushSync(() =>
        setInitialState((s) => ({ ...s, currentUser: undefined })),
      );
      const { search, pathname } = window.location;
      const sp = new URLSearchParams({ redirect: pathname + search });
      history.replace({ pathname: loginPath, search: sp.toString() });
    }
  };

  const startLineLinkAction = async () => {
    try {
      const data = await startLineLink();
      if (!data?.authorizeUrl) {
        message.error('LINE連携URLの取得に失敗しました');
        return;
      }
      window.location.href = data.authorizeUrl;
    } catch (e: any) {
      console.error(e);

      // request はエラー時に response を持つことがある
      const status = e?.response?.status;
      if (status === 401) {
        message.error('ログインが切れています。再ログインしてください');
        return;
      }
      if (status === 403) {
        message.error('LINE連携の権限がありません');
        return;
      }
      message.error('LINE連携の開始に失敗しました');
    }
  };

  const onMenuClick: MenuProps['onClick'] = ({ key }) => {
    if (key === 'lineLink') {
      startLineLinkAction();
      return;
    }
    if (key === 'logout') {
      doLogout();
      return;
    }
    if (key === 'settings') {
      setSettingsModalOpen(true);
    }
  };

  const loading = (
    <span className={styles.action}>
      <Spin size="small" style={{ marginLeft: 8, marginRight: 8 }} />
    </span>
  );

  if (!initialState?.currentUser) return loading;

  const user = initialState.currentUser;
  const isLineLinked = (() => {
    const v = user.lineId;
    if (v == null) return false;
    const s = String(v).trim().toLowerCase();
    if (!s) return false;
    if (s === 'null' || s === 'undefined') return false;
    return true;
  })();
  const items: MenuProps['items'] = [
    { key: 'settings', icon: <SettingOutlined />, label: 'ユーザー編集' },

    // ✅ LINE未連携の場合のみ表示（連携済みなら非表示）
    ...(isLineLinked
      ? []
      : [{ key: 'lineLink', icon: <LinkOutlined />, label: 'LINE連携' }]),

    { key: 'logout', icon: <LogoutOutlined />, label: 'ログアウト' },
  ];

  const isStaff = user.role === 'admin' || user.role === 'worker';
  const commentText =
    user.comment && String(user.comment).trim()
      ? String(user.comment).trim()
      : 'コメントは未入力です';

  return (
    <>
      <Dropdown
        menu={{ items, onClick: onMenuClick }}
        trigger={['click']}
        placement="bottomRight"
      >
        <button
          type="button"
          className={styles.action}
          style={{ background: 'transparent', border: 0 }}
        >
          {isStaff ? (
            // ✅ admin / worker のみ画像＋コメント表示
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Avatar
                size={32}
                src={user.photoUrl || undefined}
                icon={<UserOutlined />}
              />
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  lineHeight: 1.2,
                }}
              >
                <span style={{ fontWeight: 700 }}>
                  {user.name}
                  {user.role ? `（${ROLE_LABEL[user.role] ?? user.role}）` : ''}
                </span>

                <Typography.Text
                  type="secondary"
                  style={{
                    fontSize: 12,
                    maxWidth: 200,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {commentText}
                </Typography.Text>
              </div>
            </div>
          ) : (
            // ❌ その他ロールは名前のみ
            <span>
              {user.name}
              {user.role ? `（${ROLE_LABEL[user.role] ?? user.role}）` : ''}
            </span>
          )}
        </button>
      </Dropdown>

      <ModalForm
        title="ユーザー編集"
        width={400}
        open={settingsModalOpen}
        onOpenChange={setSettingsModalOpen}
        modalProps={{
          destroyOnClose: true,
          afterOpenChange: (open) => {
            if (open) {
              if (user.photoUrl) {
                setPreviewUrl(user.photoUrl);
                setFileList([
                  {
                    uid: '-1',
                    name: 'image.png',
                    status: 'done',
                    url: user.photoUrl,
                  },
                ]);
              } else {
                setPreviewUrl(undefined);
                setFileList([]);
              }
            }
          },
        }}
        initialValues={{
          name: user.name,
          mail: user.mail,
          tel: user.tel,
          role: user.role,
          comment: user.comment,
        }}
        onFinish={async (values) => {
          try {
            setUploading(true);
            let photoUrl = user.photoUrl;
            const file = fileList[0]?.originFileObj;
            if (file) {
              const res = await uploadImage(file, 'profile');
              photoUrl = res.url;
            }

            await putUser({
              ...user,
              ...values,
              photoUrl,
            } as any);
            message.success('アカウント情報を更新しました');
            setInitialState((s) => ({
              ...s,
              currentUser: { ...s?.currentUser, ...values, photoUrl },
            }));
            return true;
          } catch (error) {
            message.error('更新に失敗しました');
            return false;
          } finally {
            setUploading(false);
          }
        }}
      >
        <ProFormSelect
          name="role"
          label="権限"
          readonly
          valueEnum={ROLE_LABEL}
        />
        <ProFormItem label="プロフィール画像">
          <Upload
            listType="picture-card"
            maxCount={1}
            fileList={fileList}
            beforeUpload={async (file) => {
              const url = await fileToDataUrl(file);
              setPreviewUrl(url);
              setFileList([
                {
                  uid: file.uid,
                  name: file.name,
                  status: 'done',
                  originFileObj: file,
                  thumbUrl: url,
                },
              ]);
              return false;
            }}
            onRemove={() => {
              setFileList([]);
              setPreviewUrl(undefined);
            }}
          >
            <div>
              <div style={{ marginTop: 8 }}>アップロード</div>
            </div>
          </Upload>
        </ProFormItem>
        <ProFormTextArea
          name="comment"
          label="一言コメント"
          fieldProps={{ rows: 3 }}
        />
      </ModalForm>
    </>
  );
};
